import { defineStore } from 'pinia';
import { DEFAULT_AWARD_CODE } from '@/utils/awardCode';
import { splitPayKeys } from '../../shared/pay-docs/index.js';
import { weekStartsOn } from '../../shared/org-week/index.js';
import { orgScheduleLayout } from '../utils/scheduleLayout.js';

export const token = defineStore('organisationId', {
  state: () => ({
    organisationId: '',
    // Org-resolution race fix (2026-08-09) — organisationId === '' used to
    // double as both "haven't looked yet" and "looked, user has no org",
    // which let guards make a terminal no-organisation decision during the
    // transient window between a fresh login and handleUserLogin's org
    // lookup actually completing. This tri-state disambiguates them:
    // 'unknown' (default/reset) = lookup not yet run or in flight,
    // 'resolved-none' = lookup ran, confirmed no org, 'resolved-org' =
    // lookup ran, org confirmed. Guards must route 'unknown' through
    // post-login (where the lookup runs), never to dashboard/no-organisation.
    orgResolution: 'unknown',
    // MA000119 Phase 1, Task C (2026-08-11) — the current org's chosen
    // award (organisations/{orgId}.defaultAwardCode), populated by
    // usePostLoginHandler.js from the SAME org-doc snapshot it already
    // fetches to resolve organisationId (no extra Firestore read). Defaults
    // to DEFAULT_AWARD_CODE so any read before login resolves, or for an
    // org that has never set the field, behaves exactly as it did before
    // this field existed.
    defaultAwardCode: DEFAULT_AWARD_CODE,
    // TICKET 2 (2026-08-16) — org-level staff self-service toggles, same
    // login-time hydration pattern as defaultAwardCode above (populated by
    // usePostLoginHandler.js from the same org-doc snapshot, no extra read).
    // Defaults all-enabled so any read before login resolves, or for an org
    // that has never set featureFlags, behaves exactly as it did before this
    // field existed.
    featureFlags: { leaveRequests: true, sickCallIn: true },
    // W1 (Cave Inn brief, 2026-11-05) — the org's week start
    // (organisations/{orgId}.weekStartsOn, 0 = Sunday … 6 = Saturday), same
    // login-time hydration as featureFlags. Read through
    // shared/org-week :: weekStartsOn, never directly, so a stored value that
    // is not an integer 0–6 still means Sunday. Default 0, today's behaviour.
    weekStartsOn: 0,
    // B3 (2026-11-05) — the organisation's default Schedule layout
    // (organisations/{orgId}.scheduleLayout, 'area' | 'person'), same login-time
    // hydration. Anything else means 'area'. A person's own choice in this
    // browser wins over it (src/utils/scheduleLayout.js).
    scheduleLayout: 'area',
  }),
  actions: {
    organisationIdChange(newOrgId) {
      this.organisationId = newOrgId;
    },
    orgResolutionChange(newResolution) {
      this.orgResolution = newResolution;
    },
    defaultAwardCodeChange(newAwardCode) {
      this.defaultAwardCode = newAwardCode;
    },
    featureFlagsChange(newFeatureFlags) {
      this.featureFlags = newFeatureFlags;
    },
    weekStartsOnChange(newWeekStartsOn) {
      this.weekStartsOn = weekStartsOn({ weekStartsOn: newWeekStartsOn });
    },
    scheduleLayoutChange(newLayout) {
      this.scheduleLayout = orgScheduleLayout({ scheduleLayout: newLayout });
    },
  },
  persist: true,
});

export const userToken = defineStore('permissions', {
  state: () => ({
    permissions: '',
  }),
  actions: {
    permissionsChange(newPermissions) {
      this.permissions = newPermissions;
    },
  },
  persist: true,
});

export const uidToken = defineStore('uid', {
  state: () => ({
    uid: '',
  }),
  actions: {
    uidChange(newUid) {
      this.uid = newUid;
    },
  },
  persist: true,
});

export const useTemplateStore = defineStore('templateStore', {
  state: () => ({
    template: {},
  }),
  actions: {
    // S1 (brief-security-pass, 2026-10-30): this store persists to
    // localStorage, so a copied shift must carry no pay key — `cost`,
    // `costBreakdown`, `costFlags` are dropped here (shared/pay-docs ::
    // splitPayKeys) whatever the caller built. Calendar's pasteShift
    // re-prices every paste and writes shiftCosts/{shiftId} itself.
    copyTemplate(shift) {
      this.template = splitPayKeys({ ...shift }).rest;
    },
    getTemplate() {
      return this.template;
    },
    clearTemplate() {
      this.template = {};
    },
  },
  persist: true,
});
